import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Divider,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  PlayArrow as LoadIcon,
  Add as AddIcon,
  DataObject as MockDataIcon,
} from '@mui/icons-material';
import { useRecordContext, useUpdate, useNotify } from 'react-admin';

interface SavedMockConfiguration {
  id?: string;
  name: string;
  createdAt?: Date;
  modifiedAt?: Date;
  functionMocks?: { functionId: string; mock: string }[];
}

interface MockConfigurationControlProps {
  onFunctionsUpdate?: (functions: any[]) => void;
}

const MockConfigurationControl: React.FC<MockConfigurationControlProps> = ({
  onFunctionsUpdate,
}) => {
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [configurationName, setConfigurationName] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  if (!record) {
    return null;
  }

  const savedConfigurations: SavedMockConfiguration[] = 
    record.configuration?.savedMockConfigurations || [];
  const currentConfigId = record.configuration?.currentMockConfigurationId;
  const functions = record.configuration?.functions || [];

  const getCurrentConfigurationName = () => {
    if (!currentConfigId) return null;
    const config = savedConfigurations.find(c => c.id === currentConfigId);
    return config?.name;
  };

  const getCurrentConfiguration = () => {
    if (!currentConfigId) return null;
    return savedConfigurations.find(c => c.id === currentConfigId);
  };

  const getMockFunctionCount = () => {
    return functions.filter((func: any) => func.executionProviderKey === 'mock').length;
  };

  const isCurrentConfigurationModified = () => {
    const currentConfig = getCurrentConfiguration();
    if (!currentConfig) return false;
    
    const currentMockData = collectCurrentMockData();
    
    // Compare current state with saved configuration
    if (currentConfig.functionMocks?.length !== currentMockData.length) return true;
    
    return currentMockData.some(current => {
      const saved = currentConfig.functionMocks?.find(saved => saved.functionId === current.functionId);
      return !saved || saved.mock !== current.mock;
    });
  };

  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2, 15);
  };

  const collectCurrentMockData = () => {
    return functions.map((func: any) => ({
      functionId: func.id || func._id,
      mock: func.mock || '',
    }));
  };

  const handleSetAllToMock = async () => {
    try {
      console.log('MockConfigurationControl: handleSetAllToMock: setting all functions to mock');
      const updatedFunctions = functions.map((func: any) => ({
        ...func,
        executionProviderKey: 'mock',
        updatedAt: new Date().toISOString(),
      }));

      await update('application', {
        id: record.id,
        data: {
          ...record,
          configuration: {
            ...record.configuration,
            functions: updatedFunctions,
          },
        },
        previousData: record,
      });

      setSnackbarMessage('All functions set to mock mode');
      setSnackbarOpen(true);
      
      if (onFunctionsUpdate) {
        console.log('MockConfigurationControl: handleSetAllToMock: calling onFunctionsUpdate');
        onFunctionsUpdate(updatedFunctions);
      }
    } catch (error) {
      console.error('Error setting functions to mock:', error);
      notify('Failed to set functions to mock', { type: 'error' });
    }
  };

  const handleUpdateCurrentConfiguration = async () => {
    const currentConfig = getCurrentConfiguration();
    if (!currentConfig) return;

    try {
      const functionMocks = collectCurrentMockData();
      const updatedConfiguration = {
        ...currentConfig,
        modifiedAt: new Date(),
        functionMocks,
      };

      const updatedConfigurations = savedConfigurations.map(config =>
        config.id === currentConfigId ? updatedConfiguration : config
      );
      
      await update('application', {
        id: record.id,
        data: {
          ...record,
          configuration: {
            ...record.configuration,
            savedMockConfigurations: updatedConfigurations,
          },
        },
        previousData: record,
      });

      setSnackbarMessage(`Configuration "${currentConfig.name}" updated successfully`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating configuration:', error);
      notify('Failed to update configuration', { type: 'error' });
    }
  };

  const handleSaveConfiguration = async () => {
    if (!configurationName.trim()) {
      notify('Please enter a configuration name', { type: 'warning' });
      return;
    }

    try {
      const functionMocks = collectCurrentMockData();
      const newConfiguration: SavedMockConfiguration = {
        id: generateId(),
        name: configurationName.trim(),
        createdAt: new Date(),
        modifiedAt: new Date(),
        functionMocks,
      };

      const updatedConfigurations = [...savedConfigurations, newConfiguration];
      
      await update('application', {
        id: record.id,
        data: {
          ...record,
          configuration: {
            ...record.configuration,
            savedMockConfigurations: updatedConfigurations,
          },
        },
        previousData: record,
      });

      setSaveDialogOpen(false);
      setConfigurationName('');
      setSnackbarMessage(`Configuration "${newConfiguration.name}" saved successfully`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving configuration:', error);
      notify('Failed to save configuration', { type: 'error' });
    }
  };

  const handleLoadConfiguration = async (configId: string) => {
    try {
      const config = savedConfigurations.find(c => c.id === configId);
      if (!config) {
        notify('Configuration not found', { type: 'error' });
        return;
      }

      const updatedFunctions = functions.map((func: any) => {
        const mockData = config.functionMocks?.find(fm => fm.functionId === (func.id || func._id));
        return {
          ...func,
          mock: mockData?.mock || '',
          executionProviderKey: mockData?.mock ? 'mock' : func.executionProviderKey,
          updatedAt: new Date().toISOString(),
        };
      });

      await update('application', {
        id: record.id,
        data: {
          ...record,
          configuration: {
            ...record.configuration,
            functions: updatedFunctions,
            currentMockConfigurationId: configId,
          },
        },
        previousData: record,
      });

      setSnackbarMessage(`Configuration "${config.name}" loaded successfully`);
      setSnackbarOpen(true);
      
      if (onFunctionsUpdate) {
        onFunctionsUpdate(updatedFunctions);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      notify('Failed to load configuration', { type: 'error' });
    }
  };

  const handleDeleteConfiguration = async (configId: string) => {
    try {
      const configToDelete = savedConfigurations.find(c => c.id === configId);
      if (!configToDelete) return;

      const updatedConfigurations = savedConfigurations.filter(c => c.id !== configId);
      
      const updatedData = {
        ...record,
        configuration: {
          ...record.configuration,
          savedMockConfigurations: updatedConfigurations,
          currentMockConfigurationId: 
            currentConfigId === configId ? undefined : currentConfigId,
        },
      };

      await update('application', {
        id: record.id,
        data: updatedData,
        previousData: record,
      });

      setSnackbarMessage(`Configuration "${configToDelete.name}" deleted successfully`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting configuration:', error);
      notify('Failed to delete configuration', { type: 'error' });
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Unknown';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  };

  const mockCount = getMockFunctionCount();
  const currentConfigName = getCurrentConfigurationName();
  const isModified = isCurrentConfigurationModified();
  
  const chipLabel = currentConfigName 
    ? `Mock Data (${currentConfigName}${isModified ? ' *' : ''})` 
    : mockCount > 0 
      ? `Mock Data (${mockCount}/${functions.length})`
      : 'Mock Data';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={chipLabel}
          color={mockCount > 0 ? 'warning' : 'default'}
          variant={mockCount > 0 ? 'filled' : 'outlined'}
          icon={<MockDataIcon />}
        />
        <IconButton
          size="small"
          onClick={() => setSettingsOpen(true)}
          sx={{ color: 'text.secondary' }}
        >
          <SettingsIcon />
        </IconButton>
      </Box>

      {/* Settings Dialog */}
      <Dialog 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1a1a1a', color: 'white' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#2d2d2d',
          color: 'white'
        }}>
          Mock Configuration Settings
          <IconButton onClick={() => setSettingsOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ backgroundColor: '#1a1a1a', color: 'white' }}>
          {/* Quick Actions */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSetAllToMock}
                sx={{ 
                  backgroundColor: '#ff9800', 
                  '&:hover': { backgroundColor: '#f57c00' }
                }}
              >
                Set All to Mock ({functions.length} functions)
              </Button>
              {currentConfigName && isModified && (
                <Button
                  variant="contained"
                  onClick={handleUpdateCurrentConfiguration}
                  sx={{ 
                    backgroundColor: '#2196f3', 
                    '&:hover': { backgroundColor: '#1976d2' }
                  }}
                >
                  Update "{currentConfigName}"
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setSaveDialogOpen(true)}
                sx={{ 
                  borderColor: '#4caf50', 
                  color: '#4caf50',
                  '&:hover': { borderColor: '#45a049', backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                }}
              >
                Save as New Configuration
              </Button>
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: '#444', mb: 3 }} />

          {/* Current Status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
              Current Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`${mockCount} of ${functions.length} functions using mock`} 
                color={mockCount > 0 ? 'warning' : 'default'}
                variant="filled"
              />
              {currentConfigName && (
                <Chip 
                  label={`Active: ${currentConfigName}${isModified ? ' (Modified)' : ''}`} 
                  color={isModified ? 'secondary' : 'primary'}
                  variant="filled"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: '#444', mb: 3 }} />

          {/* Saved Configurations */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
              Saved Configurations ({savedConfigurations.length})
            </Typography>
            
            {savedConfigurations.length === 0 ? (
              <Alert severity="info" sx={{ backgroundColor: '#2d2d2d', color: 'white' }}>
                No saved configurations yet. Save your current mock configuration to get started.
              </Alert>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {savedConfigurations.map((config, index) => (
                  <ListItem
                    key={config.id}
                    sx={{
                      border: '1px solid #444',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: currentConfigId === config.id ? '#2d2d2d' : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: 'white' }}>{config.name}</Typography>
                          {currentConfigId === config.id && (
                            <Chip label="Current" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ color: '#aaa' }}>
                            Created: {formatDate(config.createdAt)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#aaa' }}>
                            Functions: {config.functionMocks?.length || 0}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="load"
                        onClick={() => handleLoadConfiguration(config.id!)}
                        sx={{ mr: 1, color: '#4caf50' }}
                      >
                        <LoadIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteConfiguration(config.id!)}
                        sx={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Save Configuration Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Mock Configuration</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Configuration Name"
            fullWidth
            variant="outlined"
            value={configurationName}
            onChange={(e) => setConfigurationName(e.target.value)}
            placeholder="e.g., Default Mock Setup, Testing Config, etc."
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will save the current mock data from all {functions.length} functions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveConfiguration}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default MockConfigurationControl;
